import React, { PropsWithChildren } from 'react'
import { concatStyles, remCalc } from 'utils'

export interface DrawerManagerProps {
  forceHideNav?: boolean
  // New props for passing drawer state directly
  drawerActive?: boolean
  drawerCompact?: boolean
  secondaryDrawerPresent?: boolean
}

export type DrawerDisplayMode = 'compact' | 'expanded' | 'expandedWithSecondary'

export const DRAWER_WIDTH = 256
export const COMPACT_DRAWER_WIDTH = 60

// Keep Context for backward compatibility during transition
// but it's a no-op that logs warnings
export const DrawerManagerContext = React.createContext({
  drawerActive: false,
  drawerCompact: false,
  toggleDrawer: () => {
    console.warn(
      'DrawerManagerContext is deprecated, use useDrawer hook instead'
    )
    return
  },
  useDisplayMode: (_: DrawerDisplayMode) => {
    console.warn(
      'DrawerManagerContext is deprecated, use useDrawer hook instead'
    )
    return
  },
})

const DRAWER_LAYOUT_STYLES = 'flex m-0 box-border pt-16'

// Calculate padding based on drawer state
const calculatedPadding = (
  drawerActive: boolean,
  drawerCompact: boolean,
  secondaryDrawerPresent: boolean
) => {
  if (drawerActive) {
    return secondaryDrawerPresent
      ? drawerCompact
        ? remCalc(DRAWER_WIDTH + COMPACT_DRAWER_WIDTH)
        : remCalc(DRAWER_WIDTH * 2)
      : remCalc(DRAWER_WIDTH)
  }

  if (secondaryDrawerPresent) {
    return remCalc(DRAWER_WIDTH)
  }

  return 0
}

// The Drawer Manager component using provided props
export const DrawerManager: React.FC<PropsWithChildren<DrawerManagerProps>> = ({
  children,
  drawerActive = false,
  drawerCompact = false,
  secondaryDrawerPresent = false,
}) => {
  // Calculate padding based on the provided props
  const padding = calculatedPadding(
    drawerActive,
    drawerCompact,
    secondaryDrawerPresent
  )
  const style: React.CSSProperties = { paddingLeft: padding }

  return (
    <div style={style} className={concatStyles(DRAWER_LAYOUT_STYLES)}>
      <DrawerManagerContext.Provider
        value={{
          drawerActive,
          drawerCompact,
          toggleDrawer: () =>
            console.warn('DrawerManagerContext is deprecated'),
          useDisplayMode: () =>
            console.warn('DrawerManagerContext is deprecated'),
        }}
      >
        {children}
      </DrawerManagerContext.Provider>
    </div>
  )
}
